/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { IModuleProps, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';

import { IContractsRenewalData } from './contracts-renewal.data';
import { IContractsRenewalProps } from './contracts-renewal.props.autogenerated';
import { IContractListEntity_V2 } from '../../actions/DataServiceEntities.g';
import ContractsRenewalLinks from './components/contracts-renewal-links';

export interface IContractsRenewalViewProps extends IContractsRenewalProps<IContractsRenewalData> {
    contractsHeading: React.ReactElement;
    contractsWrapper: IModuleProps;
    contractsBeforeText: React.ReactElement | null;
    contractsTable: React.ReactElement;
    contractsAfterText: React.ReactElement | null;
    contractsLinks: React.ReactElement;
}

interface IContractReviewLinkConfig {
    contractCode?: string;
    manufacturerCode?: string;
    reviewLink?: Msdyn365.ILinkData;
}

/**
 *
 * ContractsRenewal component
 * @extends {React.PureComponent<IContractsRenewalProps<IContractsRenewalData>>}
 */
class ContractsRenewal extends React.PureComponent<IContractsRenewalProps<IContractsRenewalData>> {
    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IContractsRenewalProps<IContractsRenewalData>) {
        super(props);

        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this._renderHeading = this._renderHeading.bind(this);
        this._getContractsRowData = this._getContractsRowData.bind(this);
        this._handleHeadingChange = this._handleHeadingChange.bind(this);
        this._renderTable = this._renderTable.bind(this);
        this._generateRow = this._generateRow.bind(this);
    }

    private _renderHeading(): React.ReactElement {
        const {
            config: { heading },
            context: { request }
        } = this.props;

        if (!heading || !heading.text) {
            return <></>;
        }

        return (
            <Msdyn365.Text
                className='eve-contracts-renewal__heading'
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: this._handleHeadingChange, requestContext: request }}
            />
        );
    }

    private _isContractsLoading(): boolean {
        const contractListData = this.props.data.contractList;

        if (contractListData.result) {
            return false;
        }

        const status = contractListData.status;

        if (status === 'FAILED' || status === 'SUCCESS') {
            return false;
        }

        return true;
    }

    private _getContractsRowData(): IContractListEntity_V2[] {
        if (!this.props.data.contractList.result) {
            return [];
        }

        if (this.props.data.contractList.result && this.props.data.contractList.result.length > 0) {
            return this.props.data.contractList.result;
        }

        return [];
    }

    private _handleHeadingChange = (event: Msdyn365.ContentEditableEvent): string => {
        return (this.props.config.heading.text = event.target.value);
    };

    private _getModuleTokenReplacements(contractData?: IContractListEntity_V2): Record<string, string> {
        const accountNumber = this._getCustomerAccountNumber();
        const businessName = this._getCustomerName();
        const addressInfo = this._getAddressInfo();
        const contactInfo = this._getContactInfo();

        return {
            account: accountNumber,
            mfg_assigned_id: contractData?.ContractNumber || '',
            labor_rate: contractData?.ContractLaborRate !== undefined ? contractData.ContractLaborRate.toFixed(2) : '',
            contract_notes: contractData?.ContractNotes || '',
            district_sales_rep: contractData?.SalesRepName || '',
            business_name: businessName,
            primary_address_street: addressInfo.street,
            primary_address_city: addressInfo.city,
            primary_address_state: addressInfo.state,
            primary_address_zip: addressInfo.zip,
            main_phone: contactInfo.phone,
            main_email: contactInfo.email,
            contract_code: contractData?.ContractCode || '',
            manufacturer_code: contractData?.ManufacturerCode || '',
            manufacturer_description: contractData?.ManufacturerDescription || '',
            contract_description: contractData?.Description || '',
            contract_number: contractData?.ContractNumber || '',
            contract_date:
                contractData?.DateOfContract instanceof Date
                    ? contractData.DateOfContract.toISOString()
                    : contractData?.DateOfContract || '',
            review_status:
                contractData && this._doesContractNeedReview(contractData)
                    ? this.props.resources.contractNeedsReviewLabel
                    : this.props.resources.contractUpToDateLabel || ''
        };
    }

    private _replaceTemplateTokens(
        text: Msdyn365.RichText | undefined,
        replacements: Record<string, string>,
        encode: boolean = false
    ): Msdyn365.RichText | undefined {
        if (!text) {
            return text;
        }

        let updatedText: Msdyn365.RichText = text;

        Object.entries(replacements).forEach(([token, value]) => {
            if (!token || value === undefined) {
                return;
            }

            const replacement = encode ? encodeURIComponent(value) : value;
            const placeholder = `{${token}}`;

            while (updatedText.includes(placeholder)) {
                updatedText = updatedText.replace(placeholder, replacement);
            }
        });

        return updatedText;
    }

    private _renderRichText(richText: Msdyn365.RichText | undefined, className: string): React.ReactElement | null {
        const processedText = this._replaceTemplateTokens(richText, this._getModuleTokenReplacements());
        if (!processedText) {
            return null;
        }

        return <Msdyn365.RichTextComponent className={className} text={processedText} />;
    }

    private _generateLoadingRow(): React.ReactElement {
        const {
            resources: { contractsLoadingText }
        } = this.props;

        return (
            <div className='contracts-renewal-table__loading-message'>
                <span className='contracts-renewal-table__loading-spinner' aria-hidden='true' />
                <p>{contractsLoadingText}</p>
            </div>
        );
    }

    private _renderTable(): React.ReactElement {
        const {
            resources: {
                manufacturerLabel,
                gdnContractIdLabel,
                manufacturerContractNumberLabel,
                laborRateLabel,
                contractDescLabel,
                contractDateLabel,
                statusLabel,
                noContractsFoundText
            }
        } = this.props;

        const headingRow = [
            manufacturerLabel,
            gdnContractIdLabel,
            contractDescLabel,
            manufacturerContractNumberLabel,
            laborRateLabel,
            contractDateLabel,
            statusLabel,
            ''
        ];

        const filteredContracts = this._getFilteredContracts();
        const manufacturersWithReviewLink = new Set<string>();

        return (
            <div className='contracts-renewal-table-wrapper'>
                <ul className='contracts-renewal-table'>
                    <li className='contracts-renewal-table__header'>
                        {headingRow.map((heading, index) => (
                            <div
                                key={`${heading}-${index}`}
                                className={`contracts-renewal-table__col contracts-renewal-table__col-${index + 1}`}
                            >
                                {heading}
                            </div>
                        ))}
                    </li>

                    {this._isContractsLoading() ? (
                        <li
                            className='contracts-renewal-table__row contracts-renewal-table__row-empty contracts-renewal-table__row-loading'
                            key='loading'
                        >
                            {this._generateLoadingRow()}
                        </li>
                    ) : filteredContracts.length > 0 ? (
                        filteredContracts.map(contract => {
                            const manufacturerCode = (contract.ManufacturerCode || '').trim().toUpperCase();
                            const shouldShowReviewLink = manufacturerCode !== '' && !manufacturersWithReviewLink.has(manufacturerCode);

                            if (shouldShowReviewLink) {
                                manufacturersWithReviewLink.add(manufacturerCode);
                            }

                            return (
                                <li
                                    className='contracts-renewal-table__row'
                                    key={`${contract.ManufacturerCode || ''}-${contract.ContractCode || ''}-${contract.ContractNumber ||
                                        ''}`}
                                >
                                    {this._generateRow(contract, shouldShowReviewLink)}
                                </li>
                            );
                        })
                    ) : (
                        <li className='contracts-renewal-table__row contracts-renewal-table__row-empty' key='empty'>
                            {this._generateEmptyRow(noContractsFoundText)}
                        </li>
                    )}
                </ul>
            </div>
        );
    }

    private _getContractReviewLinkConfig(): IContractReviewLinkConfig[] | undefined {
        return this.props.context.app.config.contractReviewLinks;
    }

    private _getFilteredContracts(): IContractListEntity_V2[] {
        return this._getContractsRowData().filter(contract => this._getContractReviewLink(contract) !== undefined);
    }

    private _generateRow(contractData: IContractListEntity_V2, shouldShowReviewLink: boolean): React.ReactElement {
        const {
            resources: { contractNeedsReviewLabel, contractUpToDateLabel },
            context: { cultureFormatter }
        } = this.props;

        const {
            ManufacturerCode,
            ManufacturerDescription,
            ContractCode,
            Description,
            ContractLaborRate,
            ContractNumber,
            DateOfContract
        } = contractData;

        const needsReview = this._doesContractNeedReview(contractData);
        const statusText = needsReview ? contractNeedsReviewLabel : contractUpToDateLabel;

        return (
            <>
                <div className='contracts-renewal-table__col contracts-renewal-table__col-1' data-title='Manufacturer'>
                    <p className='contracts-renewal-table__manufacturer-code'>{ManufacturerCode}</p>
                    {ManufacturerDescription && <p className='contracts-renewal-table__manufacturer-name'>{ManufacturerDescription}</p>}
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-2' data-title='Gardner Contract ID'>
                    <p>{ContractCode}</p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-3' data-title='Contract Description'>
                    <p>{Description}</p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-4' data-title='Manufacturer Contract #'>
                    <p>{ContractNumber || ''}</p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-5' data-title='Labor Rate'>
                    <p>{cultureFormatter.formatCurrency(ContractLaborRate)}</p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-6' data-title='Contract Date'>
                    <p>{cultureFormatter.formatDate(DateOfContract)}</p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-7' data-title='Status'>
                    <p
                        className={classNames('contracts-renewal-table__status', {
                            'contracts-renewal-table__status-needs-review': needsReview
                        })}
                    >
                        {statusText}
                    </p>
                </div>

                <div className='contracts-renewal-table__col contracts-renewal-table__col-8 contracts-renewal-table__review-col'>
                    {shouldShowReviewLink ? this._renderContractReviewLink(contractData) : null}
                </div>
            </>
        );
    }

    private _generateEmptyRow(noContractsFoundText: string): React.ReactElement {
        return (
            <div className='contracts-renewal-table__empty-message'>
                <p>{noContractsFoundText}</p>
            </div>
        );
    }

    private _getContractReviewLink(contractData: IContractListEntity_V2): IContractReviewLinkConfig | undefined {
        const contractReviewLinks = this._getContractReviewLinkConfig();

        const contractCode = (contractData.ContractCode || '').trim().toUpperCase();
        const manufacturerCode = (contractData.ManufacturerCode || '').trim().toUpperCase();

        if (!contractCode || !manufacturerCode || !contractReviewLinks?.length) {
            return undefined;
        }

        return (contractReviewLinks as IContractReviewLinkConfig[]).find(link => {
            const configuredContractCode = (link.contractCode || '').trim().toUpperCase();
            const configuredManufacturerCode = (link.manufacturerCode || '').trim().toUpperCase();

            return configuredContractCode === contractCode && configuredManufacturerCode === manufacturerCode;
        });
    }

    private _renderContractReviewLink(contractData: IContractListEntity_V2): React.ReactElement | null {
        const {
            resources: { contractReviewLinkFallbackText, contractReviewLinkFallbackAriaLabel },
            context: {
                app: {
                    config: { contractReviewLinkText, contractReviewLinkAriaLabel }
                }
            }
        } = this.props;

        const reviewLinkConfig = this._getContractReviewLink(contractData);
        const reviewLink = reviewLinkConfig?.reviewLink;
        const baseUrl = reviewLink?.destinationUrl;

        if (!baseUrl) {
            return null;
        }

        const linkUrl = (baseUrl
            ? this._replaceTemplateTokens(baseUrl, this._getModuleTokenReplacements(contractData), true)
            : baseUrl) as string;
        const linkText = contractReviewLinkText || contractReviewLinkFallbackText;
        const ariaLabel = contractReviewLinkAriaLabel || contractReviewLinkFallbackAriaLabel || linkText;

        return (
            <Msdyn365.Link
                link={{
                    ariaLabel,
                    className: 'contracts-renewal-table__review-link',
                    linkText,
                    linkUrl,
                    openInNewTab: true,
                    role: 'link'
                }}
            />
        );
    }

    private _getCustomerAccountNumber(): string {
        const customer = this.props.data.customer.result;
        return customer?.AccountNumber || this.props.context.request.user?.customerAccountNumber || '';
    }

    private _getCustomerName(): string {
        const customer = this.props.data.customer.result;

        if (!customer) {
            return '';
        }

        const firstName = customer.FirstName || '';
        const middleName = customer.MiddleName || '';
        const lastName = customer.LastName || '';

        const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');

        return fullName || customer.Name || '';
    }

    private _getAddressInfo(): { street: string; city: string; state: string; zip: string } {
        const addresses = this.props.data.address.result;

        const addressToUse = addresses?.find(address => address.IsPrimary) || addresses?.[0];

        if (!addressToUse) {
            return {
                street: '',
                city: '',
                state: '',
                zip: ''
            };
        }

        return {
            street: addressToUse.Street || '',
            city: addressToUse.City || '',
            state: addressToUse.State || '',
            zip: addressToUse.ZipCode || ''
        };
    }

    private _getContactInfo(): { phone: string; email: string } {
        const contacts = this.props.data.customer.result?.Contacts;

        if (!contacts?.length) {
            return { phone: '', email: '' };
        }

        const phone = contacts.find(contact => contact.AddressTypeValue === 1 && contact.IsPrimary)?.Value || '';
        const email = contacts.find(contact => contact.AddressTypeValue === 2 && contact.IsPrimary)?.Value || '';

        return { phone, email };
    }

    private _doesContractNeedReview(contractData: IContractListEntity_V2): boolean {
        const contractValidityDate: string | undefined = this.props.context.app.config.contractValidityDate;

        const contractDate = this._parseDate(contractData.DateOfContract);
        const configuredValidityDate = this._parseDate(contractValidityDate);

        if (!contractDate || !configuredValidityDate) {
            return false;
        }

        return contractDate < configuredValidityDate;
    }

    private _parseDate(value?: string | Date): Date | undefined {
        if (!value) {
            return undefined;
        }

        const date = value instanceof Date ? value : new Date(value);

        if (Number.isNaN(date.getTime())) {
            return undefined;
        }

        return date;
    }

    private _renderLink(): React.ReactElement {
        const {
            config: { links },
            context: { request }
        } = this.props;

        if (!links || links.length < 1) {
            return <></>;
        }

        return (
            <ContractsRenewalLinks
                {...{ links, onTextChange: this._handleLinkTextChange, requestContext: request, telemetryContent: this.telemetryContent }}
            />
        );
    }

    private _handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        const { links } = this.props.config;

        if (links && links[linkIndex]) {
            links[linkIndex].linkText = event.target.value;
        }
    };

    public render(): JSX.Element | null {
        const {
            config: { className }
        } = this.props;

        const viewProps = {
            ...this.props,
            contractsHeading: this._renderHeading(),
            contractsWrapper: {
                className: classNames('eve-contracts-renewal', 'contracts-renewal-container', className),
                tag: 'div',
                moduleProps: this.props
            },
            contractsBeforeText: this._renderRichText(this.props.config.beforeTableText, 'eve-contracts-renewal__before-text'),
            contractsTable: this._renderTable(),
            contractsAfterText: this._renderRichText(this.props.config.afterTableText, 'eve-contracts-renewal__after-text'),
            contractsLinks: this._renderLink()
        };

        return this.props.renderView(viewProps);
    }
}

export default ContractsRenewal;
